// Unified error humanizer: converts raw Postgres/PostgREST errors into
// user-facing, localized messages in the active UI language.
//
// Principle: never surface a raw English DB message in a non-English UI.
// Either translate a known guard, or fall back to a polite localized generic
// message (and log the technical text to console for developers).
import i18n from "@/i18n";
import { tr } from "@/lib/tr";
import { translateGuardToken } from "@/lib/cancelDocument";
import { newErrorRef, recordTechnicalError } from "@/lib/errorDiagnostics";


type AnyErr = {
  code?: string;
  message?: string;
  error_description?: string;
  details?: string;
  hint?: string;
} | string | null | undefined;

// ---------- pattern registry ----------
const RLS_REGEX = /row[-\s]?level\s+security|violates\s+row[-\s]?level\s+security\s+policy/i;
const CLOSED_PERIOD_REGEX = /closed\/partial-closed\s+fiscal\s+period|fiscal\s+period[^.]*closed|closed\s+fiscal\s+period|period\s+is\s+closed/i;
const SOURCED_GUARD_REGEX = /auto-generated\s+from|guard_sourced|cannot\s+modify\s+.*(auto|automatically)\s+generated|generated\s+journal\s+entry/i;
const UNBALANCED_REGEX = /not\s+balanced|(debit[^.]*credit|credit[^.]*debit)[^.]*must\s+equal|debit\s*(!=|<>|does not equal)\s*credit|unbalanced\s+journal/i;
// SD019 — raw text raised by assert_period_open(company,date) trigger guard.
// Format: "Cannot modify records in a closed/partial-closed fiscal period (YYYY-MM-DD)".
// Must be checked BEFORE the generic CLOSED_PERIOD_REGEX so the installment-focused
// wording overrides the generic "modify or record entries" message.
const SD019_REGEX = /Cannot\s+modify\s+records\s+in\s+a\s+closed\/partial-closed\s+fiscal\s+period\s*\((\d{4}-\d{2}-\d{2})\)/i;

function pickSd019(msg: string): string | null {
  const m = msg.match(SD019_REGEX);
  if (!m) return null;
  const date = m[1];
  return tr(
    `لا يمكن ترحيل هذا القسط لأن تاريخه (${date}) يقع ضمن فترة محاسبية مقفلة أو مقفلة جزئياً. يجب إعادة فتح الفترة أولاً، أو مراجعة تاريخ الاستحقاق.`,
    `Cannot post this installment: its date (${date}) falls within a closed or partially closed fiscal period. Reopen the period first, or review the due date.`,
    `Impossible de valider cette échéance : sa date (${date}) se situe dans une période fiscale clôturée ou partiellement clôturée. Rouvrez d'abord la période ou vérifiez la date d'échéance.`
  );
}

// Broad permission/auth patterns raised as plain P0001 (not code=42501).
// Word boundaries prevent single-word alternatives from matching substrings.
const PERMISSION_TEXT_REGEX =
  /\b(?:permission\s+denied|insufficient\s+permissions?|not\s+authenticated|not\s+authorized|unauthorized|forbidden)\b/i;

// "Already X" — closed set of verbs to avoid over-matching narrative text.
// "already exists" (unique-violation / duplicate guards like "A pending
// deletion request already exists") maps here to the neutral "already done"
// message — NOT to the signup "email already registered" text.
const ALREADY_DONE_REGEX =
  /\balready\s+(?:posted|capitalized|closed|cancelled|canceled|activated|approved|paid|received|settled|applied|reversed|returned|exists)\b/i;

// "<Entity> not found" — capture 1-4 words before literal "not found".
const NOT_FOUND_REGEX =
  /(?:^|[:.\s(])((?:[A-Z][A-Za-z]*|[a-z]+)(?:\s+(?:[A-Z][A-Za-z]*|[a-z]+)){0,3})\s+not\s+found\b/;

// Small dictionary: known English entity names → Arabic label + grammatical gender.
// `feminine: true` selects "المطلوبة … غير موجودة"، `false` selects "المطلوب … غير موجود".
const ENTITY_AR: Record<string, { label: string; feminine: boolean }> = {
  "payment":          { label: "السداد",           feminine: false },
  "prepaid":          { label: "المصروف المقدم",   feminine: false },
  "lease":            { label: "عقد الإيجار",      feminine: false },
  "company":          { label: "الشركة",           feminine: true  },
  "row":              { label: "السجل",            feminine: false },
  "line":             { label: "السطر",            feminine: false },
  "asset":            { label: "الأصل",            feminine: false },
  "project":          { label: "المشروع",          feminine: false },
  "period":           { label: "الفترة",           feminine: true  },
  "subperiod":        { label: "الفترة الفرعية",   feminine: true  },
  "fiscal period":    { label: "الفترة المحاسبية", feminine: true  },
  "tax period":       { label: "الفترة الضريبية",  feminine: true  },
  "ticket":           { label: "التذكرة",          feminine: true  },
  "subscription":     { label: "الاشتراك",         feminine: false },
  "custody":          { label: "العهدة",           feminine: true  },
  "accrual":          { label: "الاستحقاق",        feminine: false },
  "invoice":          { label: "الفاتورة",         feminine: true  },
  "stock take":       { label: "الجرد",            feminine: false },
  "warehouse":        { label: "المستودع",         feminine: false },
  "role":             { label: "الدور",            feminine: false },
  "recognition row":  { label: "سجل الاستحقاق",    feminine: false },
  "depreciation row": { label: "سجل الإهلاك",      feminine: false },
  "source jv":        { label: "قيد المصدر",       feminine: false },
  "parent account":   { label: "الحساب الأب",      feminine: false },
  "account":          { label: "الحساب",           feminine: false },
};
const DATE_REGEX = /\d{4}-\d{2}-\d{2}/;

function extractDate(msg: string): string | null {
  const m = msg.match(DATE_REGEX);
  return m ? m[0] : null;
}

function pickPermissionDenied(): string {
  return tr(
    "ليس لديك صلاحية لتنفيذ هذا الإجراء. تواصل مع مدير الحساب لمنحك الصلاحية المناسبة.",
    "You don't have permission to perform this action. Please contact your account administrator to grant you access.",
    "Vous n'avez pas l'autorisation d'effectuer cette action. Veuillez contacter l'administrateur du compte pour obtenir l'accès."
  );
}

function pickClosedPeriod(
  date: string | null,
  periodType?: "subperiod" | "fiscal_year" | "tax" | null
): string {
  const suffix = date ? ` (${date})` : "";
  if (periodType === "subperiod") {
    return tr(
      `لا يمكن تعديل أو تسجيل عمليات في فترة فرعية مقفلة${suffix}.`,
      `Cannot modify or record entries in a closed sub-period${suffix}.`,
      `Impossible de modifier ou enregistrer des opérations dans une sous-période clôturée${suffix}.`
    );
  }
  if (periodType === "tax") {
    return tr(
      `لا يمكن تعديل أو تسجيل عمليات في فترة ضريبية مقفلة${suffix}. أعد فتح الفترة الضريبية أولاً.`,
      `Cannot modify or record entries in a closed tax period${suffix}. Reopen the tax period first.`,
      `Impossible de modifier ou enregistrer des opérations dans une période fiscale (TVA) clôturée${suffix}. Réouvrez d'abord la période.`
    );
  }
  if (periodType === "fiscal_year") {
    return tr(
      `لا يمكن تعديل أو تسجيل عمليات في سنة مالية مقفلة${suffix}.`,
      `Cannot modify or record entries in a closed fiscal year${suffix}.`,
      `Impossible de modifier ou enregistrer des opérations dans un exercice fiscal clôturé${suffix}.`
    );
  }

  return tr(
    `لا يمكن تعديل أو تسجيل عمليات في فترة محاسبية مقفلة. الفترة${suffix} مغلقة.`,
    `Cannot modify or record entries in a closed fiscal period${suffix}.`,
    `Impossible de modifier ou enregistrer des opérations dans une période fiscale clôturée${suffix}.`
  );
}


function pickSourcedGuard(): string {
  return tr(
    "لا يمكن تعديل هذا المستند لأنه مولّد آلياً. عدّل المستند المصدر بدلاً منه.",
    "This document cannot be modified because it was auto-generated. Edit the source document instead.",
    "Ce document ne peut pas être modifié car il est généré automatiquement. Modifiez plutôt le document source."
  );
}

function pickUnbalanced(): string {
  return tr(
    "القيد غير متوازن: إجمالي المدين يجب أن يساوي إجمالي الدائن.",
    "Journal entry is not balanced: total debit must equal total credit.",
    "L'écriture n'est pas équilibrée : le total débit doit être égal au total crédit."
  );
}

function isInternalAdminRoute(): boolean {
  try {
    return typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
  } catch {
    return false;
  }
}

function pickGenericFailure(): string {

  return tr(
    "تعذّر تنفيذ العملية. تحقّق من البيانات أو تواصل مع الدعم.",
    "The operation could not be completed. Please check your data or contact support.",
    "Impossible d'effectuer l'opération. Vérifiez vos données ou contactez le support."
  );
}

function pickAlreadyDone(): string {
  return tr(
    "تم تنفيذ هذا الإجراء بالفعل، لا حاجة لتكراره.",
    "This action has already been completed — no need to repeat it.",
    "Cette action a déjà été effectuée — inutile de la répéter."
  );
}

function pickNotFound(entityRaw: string | null): string {
  const key = (entityRaw || "").trim().toLowerCase();
  let entry: { label: string; feminine: boolean } | null = null;
  if (key) {
    const parts = key.split(/\s+/);
    // جرّب بادئات من اليسار بترتيب تنازلي في الطول: الأطول أولاً
    // (يمسك العبارات المركّبة مثل "recognition row"، "fiscal period")،
    // ثم يتراجع للأقصر حتى الكلمة الأولى (مثل "company" في "company acme corp").
    for (let len = parts.length; len >= 1; len--) {
      const sub = parts.slice(0, len).join(" ");
      if (ENTITY_AR[sub]) { entry = ENTITY_AR[sub]; break; }
    }
  }
  if (entry) {
    const arText = entry.feminine
      ? `${entry.label} المطلوبة غير موجودة.`
      : `${entry.label} المطلوب غير موجود.`;
    return tr(
      arText,
      `The requested ${entityRaw} was not found.`,
      `L'élément demandé (${entityRaw}) est introuvable.`
    );
  }
  return tr(
    "العنصر أو المستند المطلوب لم يُعثر عليه. تحقق من الرابط أو حدّث الصفحة.",
    "The requested item or document could not be found. Check the link or refresh the page.",
    "L'élément ou le document demandé est introuvable. Vérifiez le lien ou actualisez la page."
  );
}

// ---------- Batch 2: business logic codes & specific guards ----------

const SD_CODE_REGEX = /\bSD(\d{3})((?:\|[^|]+?)*?)(?=$|[.\n;])/;
const RWH_CODE_REGEX = /\bRWH(\d{2})((?:\|[^|]+?)*?)(?=$|[.\n;])/;

function pickSdCode(msg: string): string | null {
  // SD017 uses a human-readable format ("SD017: ... - key=val ...") rather than
  // pipe-separated parts, so the generic SD_CODE_REGEX below won't match it.
  if (/\bSD017\b/.test(msg)) {
    return tr(
      "لا يمكن ترحيل هذا الشهر، يوجد قيد إطفاء مرحَّل بالفعل لنفس الشهر لهذا المصروف المقدم. راجع سجل القيود لتفادي الازدواج.",
      "Cannot post: a prepaid amortization entry already exists for this month. Review the journal to avoid duplicate posting.",
      "Impossible de valider : une écriture d'amortissement existe déjà pour ce mois. Vérifiez le journal pour éviter le doublon."
    );
  }
  // SD020 uses a colon-suffixed format ("SD020: Opening balances locked …") which
  // the pipe-oriented SD_CODE_REGEX below won't capture.
  if (/\bSD020\b/.test(msg)) {
    if (/only\s+owner\s+or\s+admin/i.test(msg)) {
      return tr(
        "الأرصدة الافتتاحية مقفلة. فقط المالك أو المدير يمكنه تجاوز القفل.",
        "Opening balances are locked. Only the owner or an admin can override the lock.",
        "Les soldes d'ouverture sont verrouillés. Seul le propriétaire ou un administrateur peut passer outre."
      );
    }
    const dm = msg.match(/from\s+(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})/i);
    const fromDate = dm?.[1] ?? "";
    const toDate = dm?.[2] ?? "";
    return tr(
      `الأرصدة الافتتاحية مقفلة (توجد فترة محاسبية مقفلة من ${fromDate} إلى ${toDate}). يتطلب تعديلها موافقة إدارية مع سبب.`,
      `Opening balances are locked (a fiscal period is closed from ${fromDate} to ${toDate}). Modification requires an admin override with a reason.`,
      `Les soldes d'ouverture sont verrouillés (période fiscale clôturée du ${fromDate} au ${toDate}). Toute modification exige une dérogation administrative motivée.`
    );
  }
  const m = msg.match(SD_CODE_REGEX);
  if (!m) return null;
  const code = `SD${m[1]}`;
  const parts = m[2] ? m[2].split("|").slice(1) : [];
  switch (code) {
    case "SD001":
      return tr("المستند المطلوب إلغاؤه غير موجود.", "Document to cancel not found.", "Document à annuler introuvable.");
    case "SD002":
      return tr("لا يمكن إلغاء مستند غير مرحّل.", "Cannot cancel a non-posted document.", "Impossible d'annuler un document non validé.");
    case "SD005": {
      const inv = parts.find((p) => /^[A-Z]{2,}-/.test(p)) || null;
      const suffix = inv ? ` (${inv})` : "";
      return tr(
        `لا يمكن إلغاء السند لأنه مرتبط بفاتورة نشطة${suffix}. ألغِ الفاتورة أولاً.`,
        `Cannot cancel: voucher is linked to an active invoice${suffix}. Cancel the invoice first.`,
        `Impossible d'annuler : bon lié à une facture active${suffix}. Annulez d'abord la facture.`
      );
    }
    case "SD006":
      return pickSourcedGuard();
    case "SD007": {
      const date = parts.find((p) => DATE_REGEX.test(p)) || null;
      const typeRaw = parts.find((p) => /^(subperiod|fiscal_year|fiscal|year|tax)$/i.test(p));
      const periodType: "subperiod" | "fiscal_year" | "tax" | null = typeRaw
        ? /^subperiod$/i.test(typeRaw)
          ? "subperiod"
          : /^tax$/i.test(typeRaw)
            ? "tax"
            : "fiscal_year"
        : null;
      return pickClosedPeriod(date, periodType);
    }
    case "SD008": {
      const state = parts[1] || parts[0] || "";
      if (state === "canceled")
        return tr(
          "الاشتراك ملغى، لذلك التسجيل والترحيل متوقفان. القراءة والتقارير والتصدير ما زالت متاحة. أعد تفعيل الاشتراك من صفحة الفواتير والاشتراكات.",
          "The subscription is canceled, so recording and posting are paused. Reading, reports and export remain available. Reactivate from the billing page.",
          "L'abonnement est annulé, la saisie et la validation sont suspendues. Lecture, rapports et export restent disponibles. Réactivez depuis la page de facturation."
        );
      if (state === "past_due_locked")
        return tr(
          "انتهت مهلة السداد، لذلك التسجيل والترحيل متوقفان. القراءة والتقارير والتصدير ما زالت متاحة. أكمل الدفع لإعادة التفعيل.",
          "The payment grace period has ended, so recording and posting are paused. Reading, reports and export remain available. Complete the payment to resume.",
          "Le délai de paiement est écoulé, la saisie et la validation sont suspendues. Lecture, rapports et export restent disponibles. Réglez le paiement pour reprendre."
        );
      return tr(
        "انتهى اشتراكك أو فترتك التجريبية، لذلك التسجيل والترحيل متوقفان. القراءة والتقارير والتصدير ما زالت متاحة. اختر باقة لإعادة التفعيل.",
        "Your subscription or trial has ended, so recording and posting are paused. Reading, reports and export remain available. Choose a plan to resume.",
        "Votre abonnement ou essai a expiré, la saisie et la validation sont suspendues. Lecture, rapports et export restent disponibles. Choisissez un forfait pour reprendre."
      );
    }
    case "SD009": {
      const kind = parts[0] || "";
      const limit = parts[1] || "";
      if (kind === "PLAN_LIMIT_COMPANIES")
        return tr(
          `بلغت الحد الأقصى لعدد الشركات في باقتك (${limit}). ارفع الباقة لإضافة شركة جديدة.`,
          `You reached your plan limit for companies (${limit}). Upgrade your plan to add another company.`,
          `Vous avez atteint la limite de sociétés de votre forfait (${limit}). Passez à un forfait supérieur pour en ajouter une autre.`
        );
      if (kind === "PLAN_LIMIT_USERS")
        return tr(
          `بلغت الحد الأقصى لعدد المستخدمين في باقتك (${limit}). ارفع الباقة لإضافة مستخدم جديد.`,
          `You reached your plan limit for users (${limit}). Upgrade your plan to add another user.`,
          `Vous avez atteint la limite d'utilisateurs de votre forfait (${limit}). Passez à un forfait supérieur pour en ajouter un autre.`
        );
      return tr(
        `بلغت الحد الأقصى لعدد القيود المرحّلة هذا الشهر في باقتك (${limit}). ارفع الباقة للمتابعة.`,
        `You reached your plan limit for posted entries this month (${limit}). Upgrade your plan to continue.`,
        `Vous avez atteint la limite d'écritures validées ce mois-ci (${limit}). Passez à un forfait supérieur pour continuer.`
      );
    }


    case "SD011": {
      const inv = parts.find((p) => /^[A-Z]{2,}-/.test(p)) || null;
      const arInv = inv ? ` ${inv}` : "";
      const enInv = inv ? ` ${inv}` : "";
      const frInv = inv ? ` ${inv}` : "";
      return tr(
        `لا يمكن إلغاء الفاتورة${arInv} لأن المخزون استُهلك في عمليات لاحقة. ألغِ العمليات التابعة أولاً.`,
        `Cannot cancel invoice${enInv}: inventory has been consumed by subsequent operations. Cancel the dependent operations first.`,
        `Impossible d'annuler la facture${frInv} : le stock a été consommé par des opérations ultérieures. Annulez d'abord les opérations dépendantes.`
      );
    }
    case "SD012":
      return tr(
        "لا يمكن إلغاء فاتورة مبيعات مرحّلة مباشرة. استخدم إشعار دائن أو مردود مبيعات للتصحيح.",
        "A posted sales invoice cannot be cancelled directly. Use a credit note or sales return to correct it.",
        "Une facture de vente validée ne peut pas être annulée directement. Utilisez un avoir ou un retour de vente pour la corriger."
      );
    case "SD013":
      return tr(
        "لا يمكن حذف مستند مُرحَّل أو تحويله لحالة أخرى مباشرة. استخدم إلغاء المستند للحفاظ على أثر المراجعة.",
        "A posted document cannot be deleted or transitioned directly. Use document cancellation to preserve the audit trail.",
        "Un document validé ne peut pas être supprimé ou transféré directement. Utilisez l'annulation du document pour préserver la piste d'audit."
      );
    case "SD014":
      return tr(
        "لا يمكن تعديل مستند مُلغى. أنشئ مستنداً جديداً بدلاً من ذلك.",
        "A cancelled document cannot be modified. Create a new document instead.",
        "Un document annulé ne peut pas être modifié. Créez un nouveau document à la place."
      );
    case "SD015":
      return tr(
        "لا يمكن إلغاء الأصل الثابت لأنه يحتوي على عملية استبعاد نشطة. ألغِ الاستبعاد أولاً ثم أعد المحاولة.",
        "Cannot cancel the fixed asset because it has an active disposal. Cancel the disposal first, then try again.",
        "Impossible d'annuler l'immobilisation : une opération de sortie est active. Annulez d'abord la sortie, puis réessayez."
      );
    case "SD016": {
      const kv = Object.fromEntries(parts.map(p => {
        const eq = p.indexOf("=");
        return eq === -1 ? [p, ""] : [p.slice(0, eq).trim(), p.slice(eq + 1).trim()];
      })) as Record<string, string>;
      const fmt = (v: string) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2) : v;
      };
      const limit     = fmt(kv.limit || "");
      const current   = fmt(kv.current || "");
      const invAmt    = fmt(kv.new || "");
      const overshoot = fmt(kv.overshoot || "");
      return tr(
        `تجاوز الحد الائتماني للعميل: الحد ${limit} — الرصيد الحالي ${current} — قيمة الفاتورة ${invAmt} — التجاوز ${overshoot}`,
        `Customer credit limit exceeded: limit ${limit} — current balance ${current} — invoice amount ${invAmt} — overshoot ${overshoot}`,
        `Limite de crédit client dépassée : limite ${limit} — solde actuel ${current} — montant de la facture ${invAmt} — dépassement ${overshoot}`
      );
    }

    case "SD017":
      return tr(
        "لا يمكن ترحيل هذا الشهر، يوجد قيد إطفاء مرحَّل بالفعل لنفس الشهر لهذا المصروف المقدم. راجع سجل القيود لتفادي الازدواج.",
        "Cannot post: a prepaid amortization entry already exists for this month. Review the journal to avoid duplicate posting.",
        "Impossible de valider : une écriture d'amortissement existe déjà pour ce mois. Vérifiez le journal pour éviter le doublon."
      );
    // SD020 handled by early-return branch above (colon-suffixed format).
    case "SD025": {
      const ref = parts.length > 1 ? parts[1] : null;
      const suffix = ref ? ` (${ref})` : "";
      return tr(
        `تعذّر ترحيل عملية دورية${suffix}. عالج العملية من بطاقة "عمليات لم تُرحّل" في لوحة التحكم ثم أعد المحاولة.`,
        `A periodic posting failed${suffix}. Resolve it from the "Operations Not Posted" card on the dashboard, then retry.`,
        `Une comptabilisation périodique a échoué${suffix}. Traitez-la depuis la carte « Opérations non comptabilisées » du tableau de bord, puis réessayez.`
      );
    }
    case "SD026": {
      const n = parts.find((p) => /^\d+$/.test(p)) || null;
      const suffix = n ? ` (${n})` : "";
      return tr(
        `لا يمكن إقفال الفترة: توجد عمليات ترحيل تلقائي فاشلة${suffix}. عالجها أولاً من بطاقة "عمليات لم تُرحّل".`,
        `Cannot close the period: there are failed automatic postings${suffix}. Resolve them first from the "Operations Not Posted" card.`,
        `Impossible de clôturer la période : des comptabilisations automatiques ont échoué${suffix}. Traitez-les d'abord depuis la carte « Opérations non comptabilisées ».`
      );
    }
    case "SD027":
      return tr(
        "لا يمكن إلغاء ترحيل فاتورة المبيعات. استخدم إشعاراً دائناً للتصحيح مع الحفاظ على أثر المراجعة.",
        "A sales invoice cannot be unposted. Use a credit note to correct it while preserving the audit trail.",
        "Une facture de vente ne peut pas être dévalidée. Utilisez un avoir afin de préserver la piste d'audit."
      );
    case "SD036":
    case "SD037":
    case "SD038":
    case "SD039":
    case "SD040": {
      const named = translateGuardToken(parts[0] || "", parts.slice(1));
      if (named) return named;
      break;
    }
    case "SD041": {
      const k = parts[0] || "";
      if (k === "FEATURE_DISABLED") return tr("إنهاء العقد غير مفعّل لهذه الشركة.", "Lease termination is not enabled for this company.", "La résiliation de bail n'est pas activée pour cette société.", "اس کمپنی کے لیے لیز کی برخاستگی فعال نہیں۔");
      if (k === "NOT_ACTIVE") return tr("يمكن إنهاء العقد الساري فقط.", "Only an active contract can be terminated.", "Seul un contrat actif peut être résilié.", "صرف فعال معاہدہ برخاست کیا جا سکتا ہے۔");
      if (k === "IFRS16_NOT_SUPPORTED") return tr("إنهاء العقد متاح حالياً للعقود بالطريقة المبسطة فقط.", "Termination is currently available for simple-method leases only.", "La résiliation n'est disponible que pour les baux en méthode simple.", "برخاستگی فی الحال صرف سادہ طریقہ والے معاہدوں کے لیے ہے۔");
      if (k === "DATE_OUT_OF_RANGE") return tr("تاريخ الإنهاء يجب أن يقع بين بداية العقد ونهايته.", "The termination date must be between the contract start and end dates.", "La date de résiliation doit être comprise entre le début et la fin du contrat.", "برخاستگی کی تاریخ معاہدے کے آغاز اور اختتام کے درمیان ہونی چاہیے۔");
      if (k === "DATE_IN_FUTURE") return tr("لا يمكن أن يكون تاريخ الإنهاء بعد اليوم.", "The termination date cannot be after today.", "La date de résiliation ne peut pas être postérieure à aujourd'hui.", "برخاستگی کی تاریخ آج کے بعد نہیں ہو سکتی۔");
      if (k === "REASON_REQUIRED") return tr("اكتب سبباً من 5 أحرف على الأقل.", "Enter a reason of at least 5 characters.", "Saisissez un motif d'au moins 5 caractères.", "کم از کم 5 حروف کی وجہ لکھیں۔");
      if (k === "MODE_INVALID") return tr("اختر طريقة تسوية صحيحة.", "Choose a valid settlement method.", "Choisissez une méthode de règlement valide.", "درست تصفیہ کا طریقہ منتخب کریں۔");
      if (k === "LOSS_ACCOUNT_INVALID") return tr("حساب الخسارة يجب أن يكون حساب مصروف فرعياً في هذه الشركة.", "The loss account must be a leaf expense account of this company.", "Le compte de perte doit être un compte de charges de détail de cette société.", "نقصان کا اکاؤنٹ اس کمپنی کا ذیلی اخراجات اکاؤنٹ ہونا چاہیے۔");
      break;
    }
    case "SD042":
      return tr("هذا العقد ليس منتهياً، فلا يوجد إنهاء للتراجع عنه.", "This contract is not terminated, so there is no termination to undo.", "Ce contrat n'est pas résilié, il n'y a donc rien à annuler.", "یہ معاہدہ برخاست نہیں، اس لیے واپس لینے کو کچھ نہیں۔");
    case "SD043":
      return tr("سجل إنهاء العقد محمي ولا يُعدّل أو يُحذف مباشرة.", "The lease termination record is protected and cannot be edited or deleted directly.", "L'enregistrement de résiliation est protégé et ne peut pas être modifié ou supprimé directement.", "برخاستگی کا ریکارڈ محفوظ ہے، اسے براہ راست تبدیل یا حذف نہیں کیا جا سکتا۔");
    case "SD035":
      if (parts[0] === "LEASE_PAYMENT_VIA_CONTRACT") {
        return translateGuardToken("LEASE_PAYMENT_VIA_CONTRACT") as string;
      }
      return tr(
        "لا يمكن تعديل بيانات الأصل بعد ترحيل أقساط استهلاك، استخدم إعادة التقدير المستقبلية أو ألغِ الأصل وأعد إصداره.",
        "Cannot modify a depreciated asset, use prospective revaluation or cancel and reissue.",
        "Impossible de modifier un actif déjà amorti, utilisez la réévaluation prospective ou annulez et réémettez.",
        "استہلاک کی اقساط پوسٹ ہونے کے بعد اثاثے کی تفصیلات تبدیل نہیں کی جا سکتیں، مستقبل کی ری ویلیوایشن استعمال کریں یا اثاثہ منسوخ کر کے دوبارہ جاری کریں۔"
      );

    case "SD030":
      return tr(
        `القيد غير متوازن (مدين ${parts[1] ?? "?"} / دائن ${parts[2] ?? "?"}) بفرق يتجاوز الحد المسموح، لذلك لم يُحفظ أي تغيير.`,
        `The journal entry is unbalanced (debit ${parts[1] ?? "?"} / credit ${parts[2] ?? "?"}) beyond the allowed tolerance, so nothing was saved.`,
        `L'écriture n'est pas équilibrée (débit ${parts[1] ?? "?"} / crédit ${parts[2] ?? "?"}) au-delà de la tolérance autorisée : rien n'a été enregistré.`
      );
    case "SD033":
      return tr(
        "لا يمكن ترحيل مستند يحتوي أصنافاً مخزنية قبل تحديد حساب المخزون الافتراضي في إعدادات الشركة.",
        "A document with stock items cannot be posted before a default inventory account is set in the company settings.",
        "Un document contenant des articles stockés ne peut pas être validé avant de définir un compte de stock par défaut dans les paramètres de la société."
      );
    case "SD034":
      return tr(
        "توجد فروق هللات في القيد، ولم يُحدَّد حساب فروق التقريب في إعدادات الشركة. حدِّده ثم أعد المحاولة.",
        "The entry has a fractional rounding difference, but no rounding variance account is set in the company settings. Set it, then retry.",
        "L'écriture présente un écart d'arrondi, mais aucun compte d'écart d'arrondi n'est défini dans les paramètres de la société. Définissez-le, puis réessayez."
      );




    default:
      return tr(
        `تعذّر إلغاء المستند (رمز: ${code}).`,
        `Cannot cancel document (code: ${code}).`,
        `Impossible d'annuler le document (code : ${code}).`
      );
  }
}

function pickRwhCode(msg: string): string | null {
  const m = msg.match(RWH_CODE_REGEX);
  if (!m) return null;
  const code = `RWH${m[1]}`;
  const parts = m[2] ? m[2].split("|").slice(1) : [];
  if (code === "RWH01") {
    const matchesPart = parts.find((p) => /^matches\s*=\s*\d+/i.test(p));
    const nMatch = matchesPart ? matchesPart.match(/\d+/) : null;
    const count = nMatch ? nMatch[0] : null;
    if (count) {
      return tr(
        `حساب المخزون مرتبط بـ${count} مستودعات. حدّد المستودع يدوياً على السطر.`,
        `Inventory account is linked to ${count} warehouses. Select the warehouse manually on the line.`,
        `Le compte de stock est lié à ${count} entrepôts. Sélectionnez l'entrepôt manuellement sur la ligne.`
      );
    }
    return tr(
      `حساب المخزون مرتبط بأكثر من مستودع. حدّد المستودع يدوياً على السطر.`,
      `Inventory account is linked to multiple warehouses. Select the warehouse manually on the line.`,
      `Le compte de stock est lié à plusieurs entrepôts. Sélectionnez l'entrepôt manuellement sur la ligne.`
    );
  }

  return null;
}

const ST_COUNTED_MISSING = /All lines must have counted_qty before posting(?:\s*\((\d+)\s*missing\))?/i;
const ST_VARIANCE_REASON = /variance_reason[^.]*(?:missing|required)/i;
const ST_NEG_BAL_REASON = /negative_balance_reason[^.]*(?:missing|required)/i;
const ST_NO_ADJ = /POST_STOCK_TAKE_NO_ADJ/i;
const ST_NO_COGS = /POST_STOCK_TAKE_NO_COGS/i;
const ST_INSUFFICIENT = /Insufficient stock for shortage/i;
const ST_STATUS = /must be in (counting|draft) status/i;
const ST_ALREADY_CANCELLED = /stock[_\s-]?take[^.]*already cancelled/i;

function pickStockTake(msg: string): string | null {
  const m = msg.match(ST_COUNTED_MISSING);
  if (m) {
    const n = m[1] ? ` (${m[1]})` : "";
    return tr(
      `لا يمكن الترحيل قبل إدخال الكميات المعدودة لجميع السطور${n}.`,
      `Cannot post: some lines are missing counted quantities${n}.`,
      `Impossible de valider : quantités comptées manquantes${n}.`
    );
  }
  if (ST_VARIANCE_REASON.test(msg))
    return tr("يجب إدخال سبب الفرق لكل سطر قبل الترحيل.", "Each line must have a variance reason before posting.", "Chaque ligne doit avoir un motif d'écart avant validation.");
  if (ST_NEG_BAL_REASON.test(msg))
    return tr("يجب إدخال سبب الرصيد السالب قبل الترحيل.", "Negative balance reason is required before posting.", "Le motif de solde négatif est requis avant validation.");
  if (ST_NO_ADJ.test(msg))
    return tr("لا يوجد حساب تسوية مخزون مضبوط للشركة. اضبطه من إعدادات الشركة قبل الترحيل.", "Company is missing the inventory adjustment account. Configure it before posting.", "Le compte d'ajustement de stock n'est pas configuré.");
  if (ST_NO_COGS.test(msg))
    return tr("لا يوجد حساب تكلفة بضاعة مباعة (COGS) مضبوط للشركة. اضبطه قبل الترحيل.", "Company is missing the COGS account. Configure it before posting.", "Le compte COGS n'est pas configuré.");
  if (ST_INSUFFICIENT.test(msg))
    return tr("لا يوجد رصيد كافٍ لتغطية العجز في الجرد.", "Insufficient stock to cover the shortage.", "Stock insuffisant pour couvrir le manque.");
  const mSt = msg.match(ST_STATUS);
  if (mSt)
    return tr(
      `يجب أن يكون الجرد في حالة "${mSt[1]}" لتنفيذ هذا الإجراء.`,
      `Stock take must be in "${mSt[1]}" status.`,
      `L'inventaire doit être au statut « ${mSt[1]} ».`
    );
  if (ST_ALREADY_CANCELLED.test(msg))
    return tr("الجرد ملغى بالفعل.", "Stock take is already cancelled.", "L'inventaire est déjà annulé.");
  return null;
}

const JE_NO_LINES = /Cannot post journal entry(?:\s+(\S+))?\s+without lines/i;
const JE_ACC_GROUP = /journal[^.]*line[^.]*(?:group account|is_group)/i;
const JE_ACC_INACTIVE = /journal[^.]*line[^.]*(?:inactive|not active)/i;
const JE_ORPHAN = /prevent_orphaning_posted|cannot[^.]*(?:remove|delete)[^.]*line[^.]*posted/i;

function pickJournalEntry(msg: string): string | null {
  const m = msg.match(JE_NO_LINES);
  if (m) {
    const no = m[1] ? ` (${m[1]})` : "";
    return tr(
      `لا يمكن ترحيل قيد بدون سطور${no}. أضف سطراً واحداً على الأقل.`,
      `Cannot post a journal entry without lines${no}. Add at least one line.`,
      `Impossible de valider une écriture sans lignes${no}. Ajoutez au moins une ligne.`
    );
  }
  if (JE_ACC_GROUP.test(msg))
    return tr("لا يمكن استخدام حساب مجموعة (رئيسي) في سطر قيد. استخدم حساباً فرعياً قابلاً للترحيل.", "Cannot use a group account on a journal line. Use a postable leaf account.", "Impossible d'utiliser un compte de groupe sur une ligne d'écriture.");
  if (JE_ACC_INACTIVE.test(msg))
    return tr("لا يمكن استخدام حساب غير نشط في سطر قيد.", "Cannot use an inactive account on a journal line.", "Impossible d'utiliser un compte inactif sur une ligne d'écriture.");
  if (JE_ORPHAN.test(msg))
    return tr("لا يمكن حذف سطور من قيد مرحّل.", "Cannot remove lines from a posted journal entry.", "Impossible de supprimer des lignes d'une écriture validée.");
  return null;
}

const COA_DEL_CHILD = /(?:has\s+child\s+accounts|has\s+sub[-\s]?accounts)/i;
const COA_DEL_JE = /(?:used\s+in|referenced\s+by)[^.]*(?:posted\s+journal|journal[^.]*posted)/i;
const COA_DEL_FK = /Cannot delete account referenced by ([\w.]+)(?:\s*\((\d+)\s*row)?/i;

function pickCoaDelete(msg: string): string | null {
  if (COA_DEL_CHILD.test(msg))
    return tr("لا يمكن حذف الحساب لوجود حسابات فرعية مرتبطة به.", "Cannot delete: account has sub-accounts.", "Impossible de supprimer : le compte a des sous-comptes.");
  if (COA_DEL_JE.test(msg))
    return tr("لا يمكن حذف الحساب لأنه مستخدم في قيود يومية مرحّلة.", "Cannot delete: account is used in posted journal entries.", "Impossible de supprimer : le compte est utilisé dans des écritures validées.");
  const m = msg.match(COA_DEL_FK);
  if (m) {
    const ref = m[1];
    const count = m[2] ? ` (${m[2]})` : "";
    return tr(
      `لا يمكن حذف الحساب لأنه مرتبط بجدول: ${ref}${count}.`,
      `Cannot delete: account is referenced by ${ref}${count}.`,
      `Impossible de supprimer : compte référencé par ${ref}${count}.`
    );
  }
  return null;
}

const WAREHOUSE_ACC_REGEX =
  /Warehouse(?:\s+(.+?))?\s+is missing the (stock-take variance account|damage\/loss expense account|default inventory account)(?:;\s*configure it.*)?/i;
const WAREHOUSE_TRANSFER_REGEX = /(Source|Destination) warehouse missing (?:default )?inventory account/i;
const WAREHOUSE_DELETE_REGEX = /Cannot delete warehouse.*movements/i;

function pickWarehouse(msg: string): string | null {
  const mAcc = msg.match(WAREHOUSE_ACC_REGEX);
  if (mAcc) {
    const whName = (mAcc[1] || "").trim();
    const kind = mAcc[2].toLowerCase();
    if (kind === "default inventory account") {
      return tr(
        "المستودع لا يحتوي على حساب المخزون الافتراضي. اضبطه من إعدادات المستودع قبل الترحيل.",
        "Warehouse is missing the default inventory account. Configure it in warehouse settings before posting.",
        "L'entrepôt n'a pas de compte de stock par défaut. Configurez-le avant la validation."
      );
    }
    const arAcc = kind === "stock-take variance account" ? "حساب فروقات الجرد" : "حساب مصروف التلف/الفاقد";
    const enAcc = kind === "stock-take variance account" ? "stock-take variance account" : "damage/loss expense account";
    const frAcc = kind === "stock-take variance account" ? "compte d'écart d'inventaire" : "compte de charge de perte/casse";
    const whAr = whName ? `المستودع "${whName}"` : "المستودع";
    const whEn = whName ? `Warehouse "${whName}"` : "The warehouse";
    const whFr = whName ? `L'entrepôt « ${whName} »` : "L'entrepôt";
    return tr(
      `${whAr} لا يحتوي على ${arAcc}. اضبطه من إعدادات المستودع قبل الترحيل.`,
      `${whEn} is missing the ${enAcc}. Configure it in warehouse settings before posting.`,
      `${whFr} n'a pas de ${frAcc}. Configurez-le avant la validation.`
    );
  }
  const mTr = msg.match(WAREHOUSE_TRANSFER_REGEX);
  if (mTr) {
    const isSource = mTr[1].toLowerCase() === "source";
    return tr(
      isSource ? "المستودع المصدر لا يحتوي على حساب المخزون. اضبطه قبل تنفيذ التحويل." : "المستودع الوجهة لا يحتوي على حساب المخزون. اضبطه قبل تنفيذ التحويل.",
      isSource ? "Source warehouse is missing its inventory account. Configure it before transferring." : "Destination warehouse is missing its inventory account. Configure it before transferring.",
      isSource ? "L'entrepôt source n'a pas de compte de stock." : "L'entrepôt de destination n'a pas de compte de stock."
    );
  }
  if (WAREHOUSE_DELETE_REGEX.test(msg))
    return tr(
      "لا يمكن حذف المستودع لوجود حركات مخزنية مرتبطة به.",
      "Cannot delete warehouse: it has inventory movements linked to it.",
      "Impossible de supprimer l'entrepôt : des mouvements de stock y sont liés."
    );
  return null;
}

// ---------- Batch 3: leases, custody, asset policy, tax accounts, admin ----------

const LEASE_ACTIVATE_REGEX =
  /Simple lease requires prepaid, payable and rent expense accounts/i;
const LEASE_PAYMENT_MISSING_REGEX =
  /Lease\s+(\S+)?\s*missing prepaid\/payable accounts/i;
const LEASE_RECOG_MISSING_REGEX =
  /Lease\s+(\S+)?\s*missing rent_expense\/prepaid accounts/i;

function pickLease(msg: string): string | null {
  if (LEASE_ACTIVATE_REGEX.test(msg))
    return tr(
      "لا يمكن تفعيل عقد الإيجار قبل ضبط حسابات: المدفوع مقدماً، الدائن (المطلوبات)، ومصروف الإيجار. أكمل إعداد الحسابات في العقد.",
      "Cannot activate the lease until the prepaid, payable, and rent-expense accounts are configured on the contract.",
      "Impossible d'activer le bail tant que les comptes charges payées d'avance, dette et charge de loyer ne sont pas configurés sur le contrat."
    );
  const mp = msg.match(LEASE_PAYMENT_MISSING_REGEX);
  if (mp) {
    const no = mp[1] ? ` (${mp[1]})` : "";
    return tr(
      `لا يمكن ترحيل دفعة الإيجار${no}: حساب المدفوع مقدماً أو الدائن غير مضبوط على العقد. أكمل إعداد الحسابات أولاً.`,
      `Cannot post lease payment${no}: prepaid or payable account is missing on the contract. Configure the accounts first.`,
      `Impossible de valider le paiement de bail${no} : compte charges payées d'avance ou dette manquant sur le contrat.`
    );
  }
  const mr = msg.match(LEASE_RECOG_MISSING_REGEX);
  if (mr) {
    const no = mr[1] ? ` (${mr[1]})` : "";
    return tr(
      `لا يمكن إثبات استحقاق الإيجار${no}: حساب مصروف الإيجار أو المدفوع مقدماً غير مضبوط على العقد.`,
      `Cannot recognize lease expense${no}: rent-expense or prepaid account is missing on the contract.`,
      `Impossible de reconnaître la charge de bail${no} : compte charge de loyer ou charges payées d'avance manquant sur le contrat.`
    );
  }
  return null;
}

const CUSTODY_CLOSED_REGEX = /Custody is closed\/cancelled/i;
const CUSTODY_EXPENSE_ACC_REGEX = /Expense account required for expense settlement/i;

function pickCashCustody(msg: string): string | null {
  if (CUSTODY_CLOSED_REGEX.test(msg))
    return tr(
      "لا يمكن تسوية العهدة لأنها مغلقة أو ملغاة.",
      "Cannot settle: the custody is closed or cancelled.",
      "Impossible de régulariser : la régie est clôturée ou annulée."
    );
  if (CUSTODY_EXPENSE_ACC_REGEX.test(msg))
    return tr(
      "يجب تحديد حساب المصروف لتسوية عهدة من نوع «مصروف».",
      "An expense account is required to settle an expense-type custody.",
      "Un compte de charge est requis pour régulariser une régie de type « charge »."
    );
  return null;
}

const ASSET_IAS8_REGEX =
  /Cannot change depreciation policy fields after depreciation has been posted/i;

function pickAssetPolicy(msg: string): string | null {
  if (ASSET_IAS8_REGEX.test(msg))
    return tr(
      "لا يمكن تعديل سياسة الإهلاك بعد ترحيل إهلاك فعلي على هذه المجموعة (وفق معيار IAS 8). أنشئ مجموعة جديدة أو استخدم قيداً تصحيحياً.",
      "Cannot change depreciation policy after depreciation has already been posted for this group (IAS 8). Create a new group or use a correcting entry instead.",
      "Impossible de modifier la politique d'amortissement après la comptabilisation d'un amortissement pour ce groupe (IAS 8)."
    );
  return null;
}

const TAX_ACC_NOT_FOUND_REGEX  = /Tax rate account\s+(\S+)?\s*not found/i;
const TAX_ACC_DIFF_COMPANY_REGEX = /Tax rate account\s+(\S+)?\s*belongs to a different company/i;
const TAX_ACC_GROUP_REGEX = /Tax rate account\s+(\S+)?\s*must be a leaf account/i;
const TAX_ACC_INACTIVE_REGEX = /Tax rate account\s+(\S+)?\s*must be active/i;

function pickTaxRateAccounts(msg: string): string | null {
  const label = (m: RegExpMatchArray | null) => (m && m[1] ? ` (${m[1]})` : "");
  let m: RegExpMatchArray | null;
  if ((m = msg.match(TAX_ACC_NOT_FOUND_REGEX)))
    return tr(
      `الحساب المرتبط بمعدل الضريبة${label(m)} غير موجود. اختر حساباً صحيحاً من دليل الحسابات.`,
      `The tax-rate account${label(m)} was not found. Select a valid account from the chart.`,
      `Le compte lié au taux de taxe${label(m)} est introuvable.`
    );
  if ((m = msg.match(TAX_ACC_DIFF_COMPANY_REGEX)))
    return tr(
      `الحساب المرتبط بمعدل الضريبة${label(m)} يتبع شركة أخرى. اختر حساباً من دليل حسابات الشركة الحالية.`,
      `The tax-rate account${label(m)} belongs to a different company. Pick an account from the current company.`,
      `Le compte lié au taux de taxe${label(m)} appartient à une autre société.`
    );
  if ((m = msg.match(TAX_ACC_GROUP_REGEX)))
    return tr(
      `الحساب المرتبط بمعدل الضريبة${label(m)} حساب مجموعة (رئيسي). اختر حساباً فرعياً قابلاً للترحيل.`,
      `The tax-rate account${label(m)} is a group account. Select a postable leaf account instead.`,
      `Le compte lié au taux de taxe${label(m)} est un compte de groupe. Choisissez un compte détail.`
    );
  if ((m = msg.match(TAX_ACC_INACTIVE_REGEX)))
    return tr(
      `الحساب المرتبط بمعدل الضريبة${label(m)} غير نشط. فعّله من دليل الحسابات أو اختر حساباً آخر.`,
      `The tax-rate account${label(m)} is inactive. Activate it or choose another account.`,
      `Le compte lié au taux de taxe${label(m)} est inactif.`
    );
  return null;
}

const ADMIN_LAST_SUPER_REGEX = /Cannot remove the last super_admin/i;
const ADMIN_OWNERS_ONLY_REGEX = /Only owners may manage company access/i;
const ADMIN_DELETE_COMPANY_REGEX = /Not authorized to delete this company/i;

function pickAdminPermission(msg: string): string | null {
  if (ADMIN_LAST_SUPER_REGEX.test(msg))
    return tr(
      "لا يمكن إزالة آخر مدير عام (super_admin) من النظام. عيّن مديراً عاماً آخر أولاً.",
      "Cannot remove the last super admin. Assign another super admin first.",
      "Impossible de supprimer le dernier super administrateur. Assignez-en un autre d'abord."
    );
  if (ADMIN_OWNERS_ONLY_REGEX.test(msg))
    return tr(
      "إدارة صلاحيات الوصول للشركات مقتصرة على مالكي مساحة العمل فقط.",
      "Only workspace owners can manage company access.",
      "Seuls les propriétaires de l'espace peuvent gérer l'accès aux sociétés."
    );
  if (ADMIN_DELETE_COMPANY_REGEX.test(msg))
    return tr(
      "غير مخوَّل بحذف هذه الشركة. تواصل مع مالك مساحة العمل.",
      "You are not authorized to delete this company. Contact the workspace owner.",
      "Vous n'êtes pas autorisé à supprimer cette société. Contactez le propriétaire de l'espace."
    );
  return null;
}

const MISC_DAYS_REGEX = /days must be\s*>\s*0/i;
const MISC_BILLING_FIELDS_REGEX =
  /Billing fields can only be modified by app administrators/i;
const MISC_SUPPORT_READONLY_REGEX = /support_messages are append-only/i;

function pickMisc(msg: string): string | null {
  if (MISC_DAYS_REGEX.test(msg))
    return tr(
      "عدد الأيام يجب أن يكون أكبر من صفر.",
      "The number of days must be greater than zero.",
      "Le nombre de jours doit être supérieur à zéro."
    );
  if (MISC_BILLING_FIELDS_REGEX.test(msg))
    return tr(
      "حقول الفوترة والاشتراك لا يمكن تعديلها إلا من قِبَل مدراء التطبيق أو العمليات الخلفية.",
      "Billing and subscription fields can only be modified by app administrators or server-side processes.",
      "Les champs de facturation ne peuvent être modifiés que par les administrateurs de l'application."
    );
  if (MISC_SUPPORT_READONLY_REGEX.test(msg))
    return tr(
      "لا يمكن تعديل رسائل الدعم بعد إرسالها — الرسائل للقراءة فقط.",
      "Support messages are read-only after they are sent.",
      "Les messages de support sont en lecture seule après envoi."
    );
  return null;
}

// SD018: unique_violation on accrued_recognition (accrual_id, period_date).
// Raised when generate_accrual_schedule tries to re-insert a month that's
// already scheduled, or a manual insert attempts a duplicate row.
const ACCRUAL_DUP_REGEX =
  /accrued_recognition_accrual_id_period_date_key|duplicate key value[^.]*accrued_recognition[^.]*(?:accrual_id|period_date)/i;

function pickAccrualDup(msg: string): string | null {
  if (!ACCRUAL_DUP_REGEX.test(msg)) return null;
  return tr(
    "يوجد بالفعل قيد استحقاق مُجدوَل لهذا الشهر لنفس المستحق. راجع السجل بدل توليد جدول مكرر.",
    "An accrual recognition row already exists for this month on the same accrual. Review the schedule instead of regenerating a duplicate.",
    "Une ligne de reconnaissance d'accumulation existe déjà pour ce mois. Vérifiez le calendrier au lieu de le régénérer."
  );
}

// unique_violation on journal entry number (uniq_je_company_entry_number_*).
function pickJournalNumberDup(msg: string): string | null {
  if (!/uniq_je_company_entry_number/i.test(msg)) return null;
  return tr(
    "رقم القيد مستخدم مسبقاً. أعد فتح القيد ليُعطى رقماً جديداً ثم حاول مرة أخرى.",
    "This journal entry number is already in use. Reopen the entry to get a new number, then try again.",
    "Ce numéro d'écriture est déjà utilisé. Rouvrez l'écriture pour obtenir un nouveau numéro, puis réessayez.",
    "یہ اندراج نمبر پہلے سے استعمال میں ہے۔ نیا نمبر لینے کے لیے اندراج دوبارہ کھولیں، پھر کوشش کریں۔",
  );
}

// Supabase Auth errors (login / signup / password reset / email verification).
// These are stable, well-known API messages — mapping them to clear localized
// text is far better than hiding them behind a generic reference code.
function pickAuthError(msg: string, code: string): string | null {
  if (/invalid[_\s]?(login[_\s]?)?credentials/i.test(msg) || code === "invalid_credentials") {
    return tr(
      "البريد الإلكتروني أو كلمة المرور غير صحيحة. تأكد من البيانات أو استخدم «نسيت كلمة المرور».",
      "Incorrect email or password. Check your details or use “Forgot password”.",
      "E-mail ou mot de passe incorrect. Vérifiez vos informations ou utilisez « Mot de passe oublié ».",
      "ای میل یا پاس ورڈ غلط ہے۔ تفصیلات چیک کریں یا «پاس ورڈ بھول گئے» استعمال کریں۔",
    );
  }
  if (/email.*not.*confirm|confirm.*email/i.test(msg) || code === "email_not_confirmed") {
    return tr(
      "لم يتم تأكيد البريد الإلكتروني بعد. افتح رسالة التحقق في بريدك ثم حاول تسجيل الدخول.",
      "Email not confirmed yet. Open the verification email, then try signing in.",
      "E-mail non confirmé. Ouvrez l'e-mail de vérification puis réessayez de vous connecter.",
      "ای میل کی تصدیق نہیں ہوئی۔ تصدیقی ای میل کھولیں پھر لاگ اِن کریں۔",
    );
  }
  if (/too many requests|rate\s*limit|over_request_rate_limit|request rate limit/i.test(msg) || code === "over_request_rate_limit") {
    return tr(
      "محاولات كثيرة متتالية. انتظر دقيقة ثم حاول مرة أخرى.",
      "Too many attempts. Wait a minute and try again.",
      "Trop de tentatives. Attendez une minute puis réessayez.",
      "بہت زیادہ کوششیں۔ ایک منٹ انتظار کریں پھر دوبارہ کوشش کریں۔",
    );
  }
  // Auth-scoped only: Supabase's literal signup messages. "already exists"
  // (unique-violation / duplicate guards) is intentionally NOT matched here —
  // it is a business error, not a login one.
  if (/user already registered|already\s+been\s+registered|already\s+registered/i.test(msg) || code === "user_already_exists") {
    return tr(
      "هذا البريد مسجّل مسبقاً. جرّب تسجيل الدخول أو استعادة كلمة المرور.",
      "This email is already registered. Try signing in or resetting your password.",
      "Cet e-mail est déjà enregistré. Essayez de vous connecter ou de réinitialiser le mot de passe.",
      "یہ ای میل پہلے سے رجسٹرڈ ہے۔ لاگ اِن کریں یا پاس ورڈ دوبارہ سیٹ کریں۔",
    );
  }
  // Must mention the password — a bare "weak"/"compromised" elsewhere in a
  // business message must not show the password advice.
  if (/\bpassword\b[^.]*(?:weak|pwned|leaked|compromis)|(?:weak|pwned|leaked|compromis)[^.]*\bpassword\b/i.test(msg) || code === "weak_password") {
    return tr(
      "كلمة المرور ضعيفة. استخدم 8 أحرف على الأقل تتضمن حروفاً كبيرة وصغيرة وأرقاماً.",
      "Password is too weak. Use at least 8 characters with upper/lowercase letters and digits.",
      "Mot de passe trop faible. Utilisez au moins 8 caractères avec majuscules, minuscules et chiffres.",
      "پاس ورڈ کمزور ہے۔ کم از کم 8 حروف، بڑے اور چھوٹے حروف اور نمبر استعمال کریں۔",
    );
  }
  if (/token.*(expired|invalid)|otp.*expired|link.*expired|email link is invalid/i.test(msg) || code === "otp_expired") {
    return tr(
      "انتهت صلاحية الرابط أو أنه غير صالح. اطلب رابطاً جديداً وحاول مرة أخرى.",
      "The link has expired or is invalid. Request a new link and try again.",
      "Le lien a expiré ou est invalide. Demandez un nouveau lien et réessayez.",
      "لنک کی میعاد ختم ہو گئی یا غلط ہے۔ نیا لنک مانگیں اور دوبارہ کوشش کریں۔",
    );
  }
  if (/signups? (not allowed|disabled)|signup.*disabled/i.test(msg) || code === "signup_disabled") {
    return tr(
      "التسجيل غير متاح حالياً. تواصل مع الدعم.",
      "Sign-up is currently unavailable. Contact support.",
      "L'inscription est actuellement indisponible. Contactez le support.",
      "رجسٹریشن فی الحال دستیاب نہیں۔ سپورٹ سے رابطہ کریں۔",
    );
  }
  return null;
}

// Heuristic: message is "raw English DB text" (mostly ASCII / no Arabic script)
// and UI is non-English → hide behind a polite generic message.
function looksRawEnglish(message: string): boolean {
  if (!message) return false;
  // Contains Arabic or French accented letters → assume already localized.
  if (/[\u0600-\u06FF]/.test(message)) return false;
  if (/[àâçéèêëîïôùûüÿœæÀÂÇÉÈÊËÎÏÔÙÛÜŸŒÆ]/.test(message)) return false;
  // Predominantly ASCII English text.
  return /[a-zA-Z]/.test(message);
}

function currentLang(): string {
  return (i18n.language || "ar").toLowerCase();
}

/**
 * Turn an unknown error (string | Error | PostgrestError | Supabase auth error)
 * into a user-facing message in the current UI language.
 */
export function humanizeError(input: AnyErr): string {
  if (input == null) return "";
  const message =
    typeof input === "string"
      ? input
      : input.message || input.error_description || input.details || "";
  const code = typeof input === "object" ? input.code || "" : "";

  // 0. Specific business-logic guards (most specific — run first)
  {
    // Batch 2
    const sd = pickSdCode(message || code); if (sd) return sd;
    const rwh = pickRwhCode(message);      if (rwh) return rwh;
    const st = pickStockTake(message);     if (st) return st;
    const je = pickJournalEntry(message);  if (je) return je;
    const coa = pickCoaDelete(message);    if (coa) return coa;
    const wh = pickWarehouse(message);     if (wh) return wh;

    // Batch 3
    const ls = pickLease(message);           if (ls) return ls;
    const cc = pickCashCustody(message);     if (cc) return cc;
    const ap = pickAssetPolicy(message);     if (ap) return ap;
    const tx = pickTaxRateAccounts(message); if (tx) return tx;
    const ad = pickAdminPermission(message); if (ad) return ad;
    const mx = pickMisc(message);            if (mx) return mx;
    // SD019 — closed/partial-closed period at posting time (installments, accruals, prepaid).
    // Must run before the generic CLOSED_PERIOD_REGEX fallback below.
    const sd19 = pickSd019(message);         if (sd19) return sd19;
    const acDup = pickAccrualDup(message);   if (acDup) return acDup;
    const jeDup = pickJournalNumberDup(message); if (jeDup) return jeDup;
  }

  // SD018 — prepaid amortization schedule guards (empty / total mismatch).
  if (/SD018/i.test(message) || /prepaid schedule/i.test(message)) {
    if (/cannot be empty/i.test(message)) {
      return tr(
        "يجب توليد جدول الإطفاء أولاً قبل الحفظ.",
        "Generate the amortization schedule before saving.",
        "Générez d'abord l'échéancier d'amortissement avant d'enregistrer.",
      );
    }
    return tr(
      "مجموع جدول الإطفاء لا يساوي إجمالي قيمة العقد. عدّل مبالغ الأقساط (مع احتساب الأقساط المرحّلة سابقاً) ثم أعد الحفظ.",
      "The amortization schedule total does not match the contract total. Adjust the instalment amounts (including already-posted instalments) and save again.",
      "Le total de l'échéancier ne correspond pas au total du contrat. Ajustez les montants des échéances (y compris celles déjà validées) puis enregistrez.",
    );
  }

  // 1. RLS / permission denied (Postgres 42501)

  if (code === "42501" || RLS_REGEX.test(message)) {
    return pickPermissionDenied();
  }

  // 2. Closed fiscal period guard
  if (CLOSED_PERIOD_REGEX.test(message)) {
    return pickClosedPeriod(extractDate(message));
  }

  // 3. Sourced-document guard (auto-generated journal entries etc.)
  if (SOURCED_GUARD_REGEX.test(message)) {
    return pickSourcedGuard();
  }

  // 4. Journal not balanced
  if (UNBALANCED_REGEX.test(message)) {
    return pickUnbalanced();
  }

  // 5. Broad permission text (raised as plain P0001, not code=42501).
  if (PERMISSION_TEXT_REGEX.test(message)) {
    return pickPermissionDenied();
  }

  // 6. "Already X" idempotency guards.
  if (ALREADY_DONE_REGEX.test(message)) {
    return pickAlreadyDone();
  }

  // 7. Generic "<Entity> not found" — must run after all more-specific patterns.
  {
    const m = message.match(NOT_FOUND_REGEX);
    if (m) return pickNotFound(m[1] || null);
  }

  // 8. Document-numbering bigint parse failure (defensive fallback for
  //    next_*_doc_no / next_*_number functions when legacy/backfilled
  //    numbers don't match the expected shape).
  if (
    /invalid input syntax for type bigint/i.test(message) &&
    /next_[a-z_]*(?:doc_no|number)|goods_receipts|inventory_doc_no/i.test(message)
  ) {
    return tr(
      "تعذّر توليد الرقم التسلسلي للمستند بسبب وجود أرقام سابقة بتنسيق غير قياسي. تواصل مع الدعم لإصلاح دالة الترقيم لهذه الشركة.",
      "Could not generate the next document number because earlier records use a non-standard numbering format. Contact support to fix the numbering function for this company.",
      "Impossible de générer le prochain numéro de document : des enregistrements existants utilisent un format non standard. Contactez le support."
    );
  }

  // 9. Supabase Auth errors (login / signup / password reset / verification).
  {
    const auth = pickAuthError(message, code);
    if (auth) return auth;
  }


  // 5. Fallback: if UI is non-English and message looks like raw English DB text,
  //    show a polite localized generic message, log the raw text for devs and
  //    record it locally under a short reference code the user can quote to
  //    support (otherwise the real cause is lost forever).
  const lang = currentLang();
  const nonEnglishUI = !lang.startsWith("en");
  if (nonEnglishUI && looksRawEnglish(message)) {
    // eslint-disable-next-line no-console
    console.error("[humanizeError] raw DB message hidden from user:", message, input);
    const ref = newErrorRef();
    recordTechnicalError({ ref, code, message });
    // Internal staff portal: never hide the technical cause — silent failures
    // there are impossible to diagnose.
    if (isInternalAdminRoute()) {
      return `${pickGenericFailure()}\n[${code || "db"}] ${message}`;
    }
    return `${pickGenericFailure()} ${tr(
      `رمز المرجع: ${ref}`,
      `Reference code: ${ref}`,
      `Code de référence : ${ref}`,
      `حوالہ کوڈ: ${ref}`,
    )}`;
  }



  return message || String(input);
}

export function isPermissionError(input: AnyErr): boolean {
  if (input == null) return false;
  const message = typeof input === "string" ? input : input.message || "";
  const code = typeof input === "object" ? input.code || "" : "";
  return code === "42501" || RLS_REGEX.test(message);
}

// Install a global interceptor on `sonner`'s toast.error so ANY string that
// flows through the app's error toasts is passed through humanizeError.
// This means the 345+ existing `toast.error(error.message)` call sites are
// automatically translated without touching each one.
let installed = false;
export function installGlobalErrorToastInterceptor(toast: any) {
  if (installed || !toast || typeof toast.error !== "function") return;
  const original = toast.error.bind(toast);
  toast.error = (message: unknown, ...rest: unknown[]) =>
    original(humanizeError(message as AnyErr) || (message as any), ...rest);
  installed = true;
  // Touch i18n once so tree-shaking keeps it wired.
  void i18n.language;
}
